import React from 'react'
import styled, { css } from 'styled-components'

import { connect } from 'react-redux'
import { I18nComponentType } from 'admin/i18n-types'
import { mapLanguageToComponentProps } from 'admin/i18n'
import { ThemeColor } from 'theme/colors'
import { Icon } from 'components/styled/icon'
import { Body } from '../typography'

export enum States {
  Draft = 'draft',
  Unpublished = 'unpublished',
  Open = 'open',
  Hidden = 'hidden',
  Cancelled = 'cancelled',
  Closed = 'closed',
  Filled = 'filled',
}

export interface StateProps {
  i18n: I18nComponentType
  state: States
  textColor?: ThemeColor
  hasBackground?: boolean
}

const BackgroundStyle = css<{ bgColor?: ThemeColor }>`
  background-color: ${({ theme, bgColor }) => bgColor && theme.color[bgColor]};
  border-radius: ${({ theme }) => theme.space.s16};
  padding-left: ${({ theme }) => theme.space.s16};
`

const StyledState = styled.div<{ bgColor?: ThemeColor }>`
  display: flex;
  align-items: center;
  width: fit-content;

  padding: ${({ theme }) => theme.space.s4} ${({ theme }) => theme.space.s16}
    ${({ theme }) => theme.space.s4} 0;
  ${({ bgColor }) => bgColor && BackgroundStyle};
`

const StateIcon = styled.div<{ color: ThemeColor }>`
  width: ${({ theme }) => theme.space.s16};
  height: ${({ theme }) => theme.space.s16};
  border-radius: ${({ theme }) => theme.space.s8};

  background-color: ${({ theme, color }) => theme.color[color]};
  color: ${({ theme }) => theme.color.white};

  display: flex;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
`

const StateBody = styled(Body)`
  margin-left: ${({ theme }) => theme.space.s8};
`

const getIcon = (state: States): string | undefined => {
  switch (state) {
    case States.Hidden:
      return 'visibility_off'
    default:
      return undefined
  }
}

const getColor = (stage: States): ThemeColor | undefined => {
  switch (stage) {
    case States.Unpublished:
    case States.Draft:
      return 'grey70'
    case States.Open:
    case States.Hidden:
      return 'secondary110'
    case States.Cancelled:
      return 'warn110'
    case States.Closed:
      return 'error110'
    case States.Filled:
      return 'success110'
    default:
      return undefined
  }
}

const getBackgroundColor = (stage: States): ThemeColor | undefined => {
  switch (stage) {
    case States.Unpublished:
    case States.Draft:
      return 'grey05O75'
    case States.Open:
    case States.Hidden:
      return 'secondary05O75'
    case States.Cancelled:
      return 'warn05O75'
    case States.Closed:
      return 'error05O75'
    case States.Filled:
      return 'success05O75'
    default:
      return undefined
  }
}

const StateFC: React.FC<StateProps> = ({
  i18n,
  state,
  textColor = 'black',
  hasBackground = false,
}) => {
  const iconType = getIcon(state)
  const color = getColor(state)
  const backgroundColor = hasBackground ? getBackgroundColor(state) : undefined
  return (
    <StyledState bgColor={backgroundColor}>
      <StateIcon color={color || 'secondary110'}>
        {iconType && <Icon type={iconType} size="s12" />}
      </StateIcon>
      <StateBody color={textColor}>{i18n.status.opening[state]}</StateBody>
    </StyledState>
  )
}

export const State = connect<{
  i18n: I18nComponentType
}>(mapLanguageToComponentProps())(StateFC)
